"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { MessageSquare } from "lucide-react";

import { clientGet } from "@/lib/api/clientApi";
import { getAssociationId } from "@/config/association";
import { useSectionStyle } from "@/components/theme-editor";
import { SmsDonationsSection } from "@/components/home/SmsDonationsSection";
import {
  EMPTY_SECTION_HEADING,
  type 
  SmsSectionData,
  SmsDonationItem,

} from "@/lib/pageBuilder/sectionsData";
import type { SmsDonation } from "@/types";

import { useSectionsBuilder } from "../SectionsBuilderProvider";
import { SectionPlaceholder } from "./SectionPlaceholder";

interface SmsSectionLiveProps {
  instanceKey: string;
  initialData: SmsSectionData | null;
}

const EMPTY_DATA: SmsSectionData = { ...EMPTY_SECTION_HEADING, items: [] };

/**
 * Live, store-driven render of the SMS donations section.
 *
 * Same priority ladder as testimonials: builder store > SSR snapshot
 * > backend fallback > placeholder. Keeping the fallback path means
 * switching on the builder flag on a page with existing backend SMS
 * records doesn't hide them â€” the admin gets a chance to override
 * per-item without starting from zero.
 */
export function SmsSectionLive({
  instanceKey,
  initialData,
}: SmsSectionLiveProps) {
  const t = useTranslations("homeSectionsBuilder");
  const hydrated = useSectionsBuilder((s) => s.hydrated);
  const storeData = useSectionsBuilder(
    (s) => s.sectionData[instanceKey] as SmsSectionData | undefined,
  );
  const themeConfig = useSectionStyle("sms");

  const data: SmsSectionData = hydrated
    ? (storeData ?? initialData ?? EMPTY_DATA)
    : (initialData ?? EMPTY_DATA);

  const builderItems = data.items.filter(
    (i) => (i.phoneNumber ?? "").trim().length > 0,
  );

  const [backendSms, setBackendSms] = useState<SmsDonation[] | null>(null);
  const associationId = getAssociationId();
  const shouldFetchBackend = builderItems.length === 0;

  useEffect(() => {
    if (!shouldFetchBackend || backendSms !== null) return;
    if (!associationId) return;
    let cancelled = false;
    clientGet<{
      data?: {
        smsDonations?: SmsDonation[] | { smsDonations?: SmsDonation[] };
      };
    }>("/api/homepage", { associationId })
      .then((resp) => {
        if (cancelled) return;
        const d = (resp.data ?? resp) as Record<string, unknown>;
        const raw = d["smsDonations"] as
          | SmsDonation[]
          | { smsDonations?: SmsDonation[] }
          | undefined;
        const list = Array.isArray(raw) ? raw : (raw?.smsDonations ?? []);
        setBackendSms(list);
      })
      .catch(() => {
        if (cancelled) return;
        setBackendSms([]);
      });
    return () => {
      cancelled = true;
    };
  }, [shouldFetchBackend, backendSms, associationId]);

  const smsForUi: SmsDonation[] = useMemo(() => {
    if (builderItems.length > 0) {
      return builderItems.map((item, idx) =>
        mapBuilderItemToBackend(item, idx),
      );
    }
    return backendSms ?? [];
  }, [builderItems, backendSms]);

  if (smsForUi.length === 0) {
    if (shouldFetchBackend && backendSms === null) return null;
    return (
      <SectionPlaceholder
        instanceKey={instanceKey}
        title={t("sections.sms.title")}
        hint={t("sections.sms.placeholderHint")}
        Icon={MessageSquare}
        aspect="banner"
      />
    );
  }

  return (
    <SmsDonationsSection
      title={data.title}
      subtitle={data.subtitle}
      showQuoteMarks={!!data.showQuoteMarks}
      quoteMarkSrc={data.quoteMarkImageUrl}
      smsDonations={smsForUi}
      layout={themeConfig.variant}
      itemsPerView={themeConfig.itemsPerView}
    />
  );
}

function mapBuilderItemToBackend(
  item: SmsDonationItem,
  index: number,
): SmsDonation {
  return {
    Id: hashKeyToNumber(item.key) + index,
    AssociationId: 0,
    PhoneNumber: item.phoneNumber ?? "",
    MessageBody: item.messageBody ?? "",
    ImageId: item.imageId ?? null,
    ImageUrl: item.imageUrl ?? null,
  };
}

function hashKeyToNumber(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = (h << 5) - h + key.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}
